/**
 * Death Zone Trigger Behavior
 * 
 * Destroys entities that enter the trigger zone.
 * Useful for creating kill zones, boundaries, or hazardous areas.
 */

import { EntityType, EntityTypeHelpers } from '../../types/EntityTypes';
import type { TriggerBehavior } from '../../types/TriggerTypes';
import { logger } from '../../core/Logger';

export const deathZoneBehavior: TriggerBehavior = {
  id: 'trigger:death_zone',
  displayName: 'Death Zone',
  type: EntityType.TRIGGER_ZONE,
  category: 'trigger',
  tags: ['hazard', 'death', 'kill'],
  
  parameters: {
    affectsPlayer: {
      type: 'boolean',
      default: true,
      description: 'Kill player on contact'
    },
    affectsNPCs: {
      type: 'boolean',
      default: true,
      description: 'Kill NPCs on contact'
    },
    affectsObjects: {
      type: 'boolean',
      default: true,
      description: 'Destroy dynamic objects on contact'
    },
    damageOverTime: {
      type: 'boolean',
      default: false,
      description: 'Apply damage over time instead of instant death'
    },
    damagePerSecond: {
      type: 'number',
      default: 50,
      min: 1,
      max: 1000,
      description: 'Damage per second (only if damage over time is enabled)'
    }
  },
  
  onEnter: (api) => {
    const { ctx, services } = api;
    const { params } = ctx;
    
    const affectsPlayer = (params.affectsPlayer as boolean) ?? true;
    const affectsNPCs = (params.affectsNPCs as boolean) ?? true;
    const affectsObjects = (params.affectsObjects as boolean) ?? false;
    const damageOverTime = (params.damageOverTime as boolean) ?? false;
    const damagePerSecond = (params.damagePerSecond as number) ?? 50;
    
    // Check if we should affect this entity
    const shouldAffect = 
      (ctx.entrant.isPlayer && affectsPlayer) ||
      (EntityTypeHelpers.isNPC(ctx.entrant) && affectsNPCs) ||
      (ctx.entrant.type === EntityType.DYNAMIC_OBJECT && affectsObjects);
    
    if (!shouldAffect) return;
    
    if (damageOverTime) {
      // Initialize metadata if needed
      if (!ctx.self.metadata) {
        (ctx.self as { metadata: Record<string, unknown> }).metadata = {};
      }
      const metadata = ctx.self.metadata!;
      
      // Track entities taking damage
      if (!metadata.damageTargets) {
        metadata.damageTargets = new Map<string, number>();
      }
      const damageTargets = metadata.damageTargets as Map<string, number>;
      
      // Start damage interval - accumulate damage over time then destroy
      let accumulatedDamage = 0;
      const damageThreshold = 100; // Destroy after accumulating this much damage
      const isPlayer = ctx.entrant.isPlayer;
      
      const intervalId = setInterval(() => {
        const damage = damagePerSecond / 10; // 10 ticks per second
        accumulatedDamage += damage;
        
        logger.triggers.log(`💀 Death zone: Dealing ${damage.toFixed(1)} damage to ${ctx.entrant.id} (${accumulatedDamage.toFixed(1)}/${damageThreshold})`);
        
        // Check if accumulated enough damage to destroy
        if (accumulatedDamage >= damageThreshold) {
          clearInterval(intervalId);
          damageTargets.delete(ctx.entrant.id);
          
          if (isPlayer) {
            // Player death - trigger respawn via pluggable handler
            services.player.onDeath('death_zone');
            logger.triggers.log(`💀 Death zone: Player died after ${accumulatedDamage.toFixed(1)} accumulated damage`);
          } else {
            // Non-player - destroy immediately
            services.spawn.disposeById(ctx.entrant.id);
            logger.triggers.log(`💀 Death zone: Destroyed ${ctx.entrant.id} after ${accumulatedDamage.toFixed(1)} accumulated damage`);
          }
        }
      }, 100); // 10 times per second
      
      damageTargets.set(ctx.entrant.id, intervalId as unknown as number);
      
      logger.triggers.log(`💀 Death zone: Started damage over time for ${ctx.entrant.id} (${damagePerSecond}/s)`);
    } else {
      // Instant death
      if (ctx.entrant.isPlayer) {
        // Player death - trigger respawn via pluggable handler
        services.player.onDeath('death_zone');
        logger.triggers.log(`💀 Death zone: Player instantly killed`);
      } else {
        // Non-player - destroy immediately
        services.spawn.disposeById(ctx.entrant.id);
        logger.triggers.log(`💀 Death zone: Instantly destroyed ${ctx.entrant.id}`);
      }
    }
  },
  
  onExit: (api) => {
    const { ctx } = api;
    
    // Stop damage over time if active
    const damageTargets = ctx.self.metadata?.damageTargets as Map<string, number> | undefined;
    if (damageTargets?.has(ctx.exiter.id)) {
      const intervalId = damageTargets.get(ctx.exiter.id);
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        damageTargets.delete(ctx.exiter.id);
        logger.triggers.log(`💀 Death zone: Stopped damage for ${ctx.exiter.id} (exited zone)`);
      }
    }
  }
};
